import { useEffect, useState } from "react";
import { api, errText } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

const fields = [
  { key: "couple_name_1", label: "Namn 1" },
  { key: "couple_name_2", label: "Namn 2" },
  { key: "wedding_date", label: "Bröllopsdatum (text)" },
  { key: "ceremony_time", label: "Tid för vigsel" },
  { key: "venue_name", label: "Plats / lokal" },
  { key: "venue_address", label: "Adress" },
  { key: "maps_query", label: "Sökfras för kartor" },
  { key: "dress_code", label: "Klädkod (lämna tom för att dölja)" },
  { key: "rsvp_deadline", label: "OSA senast (lämna tom för att dölja)", type: "date" },
  { key: "notice", label: "Meddelande till gästerna (visas under Dagen, lämna tomt för att dölja)", multiline: true },
];

export default function SettingsTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      const { key, ...rest } = data || {};
      setForm(rest);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const clean = { ...form, faqs: (form.faqs || []).filter((f) => f.q.trim() && f.a.trim()) };
      await api.put("/admin/settings", clean);
      setForm(clean);
      toast.success("Inställningar sparade");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  return (
    <div className="max-w-lg">
      <h2 className="font-serif-d text-xl font-semibold mb-6">Bröllopsuppgifter</h2>
      <div className="space-y-5">
        {fields.map((f) => (
          <div key={f.key}>
            <p className="text-[0.65rem] uppercase tracking-widest mb-1" style={{ color: "var(--ink-soft)" }}>
              {f.label}
            </p>
            {f.multiline ? (
              <textarea
                className="input-line font-sans-d text-sm resize-y min-h-[70px]"
                value={form[f.key] || ""}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                data-testid={`settings-${f.key}-input`}
              />
            ) : (
              <input className="input-line font-sans-d text-sm" type={f.type || "text"} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`settings-${f.key}-input`} />
            )}
          </div>
        ))}

        <div className="pt-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[0.65rem] uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>
              Frågor &amp; svar (visas i inbjudan)
            </p>
            <button
              className="btn-invite text-[0.55rem] px-3 py-1.5"
              onClick={() => setForm({ ...form, faqs: [...(form.faqs || []), { q: "", a: "" }] })}
              data-testid="add-faq-btn"
            >
              <Plus className="w-3 h-3" /> Lägg till
            </button>
          </div>
          <div className="space-y-4">
            {(form.faqs || []).map((f, i) => (
              <div key={i} className="p-3 relative" style={{ border: "1px solid rgba(212,185,160,0.5)", backgroundColor: "rgba(255,255,255,0.5)" }}>
                <button
                  className="absolute top-2 right-2 p-1 opacity-50 hover:opacity-100"
                  title="Ta bort"
                  onClick={() => setForm({ ...form, faqs: form.faqs.filter((_, j) => j !== i) })}
                  data-testid={`remove-faq-${i}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
                <input
                  className="input-line font-sans-d text-sm mb-2 pr-8"
                  placeholder="Fråga"
                  value={f.q}
                  onChange={(e) => setForm({ ...form, faqs: form.faqs.map((x, j) => (j === i ? { ...x, q: e.target.value } : x)) })}
                  data-testid={`faq-q-input-${i}`}
                />
                <input
                  className="input-line font-sans-d text-sm"
                  placeholder="Svar"
                  value={f.a}
                  onChange={(e) => setForm({ ...form, faqs: form.faqs.map((x, j) => (j === i ? { ...x, a: e.target.value } : x)) })}
                  data-testid={`faq-a-input-${i}`}
                />
              </div>
            ))}
          </div>
        </div>

        <button className="btn-invite btn-solid w-full" onClick={save} disabled={saving} data-testid="save-settings-btn">
          {saving ? "Sparar…" : "Spara"}
        </button>
      </div>
    </div>
  );
}
